import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ResponsiveImage from './ResponsiveImage';

export interface Artwork {
  _id: string;
  title: string;
  artist_ids: { _id: string; name: string }[];
  image_url: string;
  image_base?: string;
  price?: string;
  category?: string;
  description?: string;
  featured?: boolean;
  order?: number;
}

interface ArtworkGridProps {
  items: Artwork[];
  columns?: number;
  showFilters?: boolean;
  itemsPerPage?: number;
}

export default function ArtworkGrid({ 
  items, 
  columns = 3, 
  showFilters = false,
  itemsPerPage = 12
}: ArtworkGridProps) {
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);

  const toggleLike = (id: string) => {
    const newLiked = new Set(liked);
    if (newLiked.has(id)) {
      newLiked.delete(id);
    } else {
      newLiked.add(id);
    }
    setLiked(newLiked);
  };

  const categories = useMemo(() => {
    if (!showFilters) return [];
    const cats = new Set(items.map(item => item.category).filter(Boolean) as string[]);
    return ['All', ...Array.from(cats)];
  }, [items, showFilters]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (showFilters && activeCategory !== 'All') {
      result = items.filter(item => item.category === activeCategory);
    }
    return result;
  }, [items, activeCategory, showFilters]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const colsClass = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }[columns] || 'md:grid-cols-3';

  const imageWidth = ({ 2: 960, 3: 720, 4: 640 } as Record<number, number>)[columns] ?? 720;
  const imageHeight = Math.round(imageWidth * 1.25);
  const imageWidthsByColumn = ({
    2: [360, 540, 720, 960],
    3: [320, 480, 640, 800],
    4: [280, 420, 560, 720],
  } as Record<number, number[]>)[columns] ?? [320, 480, 640, 800];
  const imageSizesByColumn = ({
    2: '(max-width: 767px) 92vw, (max-width: 1279px) 46vw, 44vw',
    3: '(max-width: 767px) 92vw, (max-width: 1023px) 46vw, 30vw',
    4: '(max-width: 767px) 92vw, (max-width: 1023px) 30vw, 22vw',
  } as Record<number, string>)[columns] ?? '(max-width: 767px) 92vw, (max-width: 1023px) 46vw, 30vw';

  return (
    <div className="space-y-12">
      {/* Category Filters */}
      {showFilters && categories.length > 1 && (
        <div className="flex flex-wrap justify-center gap-4 border-b border-gray-100 pb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-6 py-2 text-sm uppercase tracking-widest font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-900'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className={`grid grid-cols-1 ${colsClass} lg:grid-cols-${columns} gap-8`}>
        {paginatedItems.map((artwork) => (
          <Link
            id={`artwork-${artwork._id}`}
            key={artwork._id}
            to={`/gallery/${artwork._id}`}
            className="group scroll-mt-28 block"
          >
            <div className="relative overflow-hidden bg-gray-100 aspect-[4/5] mb-6 border border-gray-100 group-hover:border-gray-300 transition-colors duration-500">
              <ResponsiveImage
                base={artwork.image_base}
                url={artwork.image_url}
                sizes={imageSizesByColumn}
                alt={artwork.title}
                width={imageWidth}
                height={imageHeight}
                cloudinaryWidths={imageWidthsByColumn}
                aspectRatio={1.25}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleLike(artwork._id);
                }}
                aria-label={`Like ${artwork.title}`}
                className="absolute top-4 right-4 p-3 rounded-full bg-white text-gray-900 shadow-sm opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-gray-50"
              >
                <Heart
                  size={20}
                  className={liked.has(artwork._id) ? 'fill-red-500 text-red-500' : 'text-gray-900'}
                  strokeWidth={1.5}
                />
              </button>
            </div>

            <div className="space-y-1 text-center">
              <h3 className="text-xl font-semibold text-gray-900 group-hover:text-gray-600 transition-colors">
                {artwork.title}
              </h3>
              <p className="text-gray-500 font-light">
                {artwork.artist_ids?.map((a) => a.name).join(' & ')}
              </p>
              {artwork.price && (
                <p className="text-lg font-medium text-gray-900 pt-2 tracking-wide">
                  {artwork.price}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Empty State */}
      {paginatedItems.length === 0 && (
        <div className="text-center py-20 bg-gray-50 border border-gray-100 rounded-xl">
          <p className="text-xl font-light text-gray-500">No artworks found in this category.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-12 border-t border-gray-100">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-3 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-500 transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={24} strokeWidth={1.5} />
          </button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-10 h-10 flex items-center justify-center text-sm font-medium transition-colors ${
                  currentPage === i + 1
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-3 text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-500 transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={24} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}